import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  List, 
  ListItem, 
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Drawer,
  InputAdornment,
  Button,
  useTheme,
  styled,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemButton,
  Link,
  Menu,
  MenuItem,
  Card,
  CardContent,
} from '@mui/material';
import {
  Layers,
  InsertDriveFile,
  Description,
  MoreVert,
  Search,
  FileCopy,
  Download,
  Share,
  Home,
  Nature,
  Menu as MenuIcon,
  School,
  Close,
  FamilyRestroom,
  Man,
  TrendingUp,
  Public,
  ExpandMore,
  Email,
  WhatsApp,
  Bluetooth,
  Groups,
  LocalHospital,
  Person,
  Grass,
  Spa,
  BugReport,
  Hive,
  Water as WaterIcon,
  Groups3,
  Handshake,
  Diversity3,
  House,
  Roofing,
  Map,
  Pets as LivestockIcon,
  EggAlt as PoultryIcon,
  Cottage as EmergencyHomeIcon, 
  AccessibilityNew, 
  ChildCare, 
  Terrain, 
  PictureAsPdf,
  Engineering 
} from '@mui/icons-material';

// Constantes de style
const drawerWidth = 240;
const primaryColor = '#E30613';
const secondaryColor = '#008D36';
const backgroundColor = '#f5f5f5';

// Composants stylisés
const MainContainer = styled('div')({
  display: 'flex',
  backgroundColor: backgroundColor,
  minHeight: '100vh',
  flexDirection: 'column'
});

const MainContentWithDrawer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  marginTop: '64px',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    marginTop: '56px'
  }
}));

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
  backgroundColor: backgroundColor,
  height: 'calc(100vh - 64px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    height: 'calc(100vh - 56px)'
  }
}));

const DrawerWrapper = styled('div')(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  overflow: 'auto',
  height: 'calc(100vh - 64px)',
  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
  backgroundColor: '#fff',
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    zIndex: 1300,
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    '&.drawerOpen': {
      transform: 'translateX(0)',
    },
    '&.drawerClose': {
      transform: 'translateX(-100%)',
    },
  }
}));

const MobileDrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const StyledTableHead = styled(TableHead)({
  backgroundColor: primaryColor,
  '& th': {
    color: 'white',
    fontWeight: 'bold'
  }
});

const ProvinceRow = styled(TableRow)({
  backgroundColor: '#e0e0e0',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#d0d0d0 !important'
  }
});

const menuItems = [
  { text: 'Ménages par type', icon: <FamilyRestroom color="primary" />, to: '/RGPHAEDataPage' },
  { text: 'Population par localisation', icon: <Public color="primary" />, to: '/PopulationDataPage' },
  { text: 'Ménages collectifs', icon: <Groups color="primary" />, to: '/PopulationCollectiveHouseholdsPage' },
  { text: 'Sans-abris', icon: <EmergencyHomeIcon color="primary" />, to: '/HomelessPopulationPage' },
  { text: 'Ménages ordinaires', icon: <Home color="primary" />, to: '/PopulationResidentPage' },
  { text: 'Sexe et masculinité', icon: <Man color="primary" />, to: '/PopulationDataPageM' },
  { text: 'Rapport de masculinité', icon: <TrendingUp color="primary" />, to: '/MasculinityRatioPage' },
  { text: 'Population par âge', icon: <ChildCare color="primary" />, to: '/PopulationDataPageAge' },
  { text: "Grands groupes d'âge", icon: <AccessibilityNew color="primary" />, to: '/PopulationAgeGroupPageGrand' },
  { text: "Groupes d'âge (éducation)", icon: <School color="primary" />, to: '/PopulationByAgeGroupPage' },
  { text: 'Groupes d\'âge (santé)', icon: <LocalHospital color="primary" />, to: '/AgeGroupPopulationPage' },
  { text: 'Adultes (18+)', icon: <Person color="primary" />, to: '/AdultPopulationPage' },
  
  // Agriculture
  { text: 'Ménages agricoles', icon: <Nature color="primary" />, to: '/AgriculturalHouseholdsPage' },
  { text: 'Pratiques agricoles', icon: <Grass color="primary" />, to: '/AgriculturalHouseholdsPages' },
  { text: 'Types de cultures', icon: <Spa color="primary" />, to: '/AgriculturalHouseholdsPage20' },
  
  // Élevage
  { text: 'Élevage par espèces', icon: <LivestockIcon color="primary" />, to: '/FarmingHouseholdsPage' },
  { text: 'Élevage de volailles', icon: <PoultryIcon color="primary" />, to: '/poultryFarmingPage' },
  { text: 'Élevage spécial', icon: <BugReport color="primary" />, to: '/insectFarmingHouseholdsPage' },
  { text: 'Répartition cheptel', icon: <LivestockIcon color="primary" />, to: '/livestockDistributionPage' },
  { text: 'Répartition volailles', icon: <PoultryIcon color="primary" />, to: '/poultryDistributionPage' },
  
  // Matériel agricole
  { text: 'Équipements agricoles', icon: <Engineering color="primary" />, to: '/agriculturalEquipmentPage' },
  { text: 'Apiculture', icon: <Hive color="primary" />, to: '/beekeepingHouseholdsPage' },
  { text: 'Pêche/aquaculture', icon: <WaterIcon color="primary" />, to: '/fishingHouseholdsPage' },
  
  // Associations
  { text: 'Interventions associations', icon: <Groups3 color="primary" />, to: '/associationInterventionPage' },
  { text: 'Interventions coopératives', icon: <Handshake color="primary" />, to: '/cooperativeInterventionPage' },
  { text: 'Interventions ONG', icon: <Diversity3 color="primary" />, to: '/interventionProjectsPage' },
  
  // Habitat
  { text: "Type d'habitat", icon: <House color="primary" />, to: '/housingDistributionPage' },
  { text: 'Matériau toit', icon: <Roofing color="primary" />, to: '/roofMaterialPage' },
  { text: 'Matériau sol', icon: <Layers color="primary" />, to: '/housingMaterialPage' },
  { text: 'Ménages agricoles', icon: <Terrain color="primary" />, to: '/agriculturalHouseholdsPageProvCom' },
  { text: 'Population par âge/résidence', icon: <Map color="primary" />, to: '/populationAgeResidencePage' },
];

// Structure des données
const beekeepingData = {
  title: "Répartition des ménages agricoles pratiquant l'apiculture selon le type par province",
  types: ["Apiculture traditionnelle", "Apiculture moderne"],
  provinces: [
    {
      name: "BUHUMUZA",
      traditionnelle: "16 723",
      moderne: "1 108",
      total: "416 068"
    },
    {
      name: "BUJUMBURA",
      traditionnelle: "5 922",
      moderne: "1 394",
      total: "461 754"
    },
    {
      name: "BURUNGA",
      traditionnelle: "6 660",
      moderne: "826",
      total: "411 362"
    },
    {
      name: "BUTANYERERA",
      traditionnelle: "15 382",
      moderne: "1 483",
      total: "552 250"
    },
    {
      name: "GITEGA",
      traditionnelle: "11 660",
      moderne: "1 692",
      total: "494 561"
    }
  ],
  total: {
    traditionnelle: "56 347",
    moderne: "6 503",
    total: "2 335 995"
  }
};

const BeekeepingHouseholdsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [openKeyStats, setOpenKeyStats] = useState(true);
  
  const handleCopyTable = () => {
    let tableContent = "Province\tApiculture traditionnelle\tApiculture moderne\tTotal ménages\n";
    
    // Ajout des données totales
    tableContent += `BURUNDI\t${beekeepingData.total.traditionnelle}\t${beekeepingData.total.moderne}\t${beekeepingData.total.total}\n`;
    
    // Ajout des données par province
    beekeepingData.provinces.forEach(province => {
      tableContent += `${province.name}\t${province.traditionnelle}\t${province.moderne}\t${province.total}\n`;
    });
    
    navigator.clipboard.writeText(`${beekeepingData.title}\n\n${tableContent}`)
      .then(() => alert('Tableau copié dans le presse-papier'))
      .catch(err => console.error('Erreur lors de la copie:', err));
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleShareMenuOpen = (event) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleDownloadMenuOpen = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setShareAnchorEl(null);
    setDownloadAnchorEl(null);
  };

  const shareVia = (method) => {
    handleMenuClose();
    const text = `Découvrez les données sur l'apiculture au Burundi: ${beekeepingData.title}`;
    const url = window.location.href;
    
    switch(method) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'bluetooth':
        alert(`Partage via Bluetooth simulé: ${text}`);
        break;
      default:
        if (navigator.share) {
          navigator.share({
            title: text,
            url: url
          }).catch(err => console.log('Erreur de partage:', err));
        } else {
          navigator.clipboard.writeText(url);
          alert('Lien copié dans le presse-papier');
        }
    }
  };

  const downloadFile = (format) => {
    handleMenuClose();
    const blob = new Blob([JSON.stringify(beekeepingData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `menages-agricoles-apiculture-burundi.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    alert(`Téléchargement du fichier ${format} simulé`);
  };

// Calcul des indicateurs clés
const keyStats = {
  totalHouseholds: beekeepingData.total.total,

  mostCommonType: beekeepingData.types.reduce((max, type) => {
    const currentTotal = parseInt(beekeepingData.total[type.toLowerCase().split(' ')[1]].replace(/\s/g, ''));
    const maxTotal = parseInt(beekeepingData.total[max.toLowerCase().split(' ')[1]].replace(/\s/g, ''));
    return currentTotal > maxTotal ? type : max;
  }, 'Apiculture traditionnelle'),

  provinceWithMostBeekeeping: beekeepingData.provinces.reduce((max, province) => {
    const total = parseInt(province.traditionnelle.replace(/\s/g, '')) + parseInt(province.moderne.replace(/\s/g, ''));
    const maxTotal = parseInt(max.traditionnelle.replace(/\s/g, '')) + parseInt(max.moderne.replace(/\s/g, ''));
    return total > maxTotal ? province : max;
  }),

  provinceWithLeastBeekeeping: beekeepingData.provinces.reduce((min, province) => {
    const total = parseInt(province.traditionnelle.replace(/\s/g, '')) + parseInt(province.moderne.replace(/\s/g, ''));
    const minTotal = parseInt(min.traditionnelle.replace(/\s/g, '')) + parseInt(min.moderne.replace(/\s/g, ''));
    return total < minTotal ? province : min;
  }),

  highestTraditionalPercentage: beekeepingData.provinces.reduce((max, province) => {
    const percentage = (
      parseInt(province.traditionnelle.replace(/\s/g, '')) /
      (parseInt(province.traditionnelle.replace(/\s/g, '')) + parseInt(province.moderne.replace(/\s/g, '')))
    ) * 100;
    return percentage > max.percentage ? { province, percentage } : max;
  }, { province: null, percentage: 0 }),

  highestModernPercentage: beekeepingData.provinces.reduce((max, province) => {
    const percentage = (
      parseInt(province.moderne.replace(/\s/g, '')) /
      (parseInt(province.traditionnelle.replace(/\s/g, '')) + parseInt(province.moderne.replace(/\s/g, '')))
    ) * 100;
    return percentage > max.percentage ? { province, percentage } : max;
  }, { province: null, percentage: 0 })
};

const filteredProvinces = beekeepingData.provinces.filter(province =>
  !searchTerm || province.name.toLowerCase().includes(searchTerm.toLowerCase())
);


  // Composant du menu latéral
  const drawer = (
    <div>
      {isMobile && (
        <MobileDrawerHeader>
          <IconButton onClick={handleDrawerToggle}>
            <Close color="primary" />
          </IconButton>
        </MobileDrawerHeader>
      )}
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.to}
              onClick={isMobile ? handleDrawerToggle : undefined}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <MainContainer>
      <CssBaseline />
      <NavigationBar />
      <MainContentWithDrawer>
        {!isMobile ? (
          <DrawerWrapper>
            {drawer}
          </DrawerWrapper>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
        )}

        <ContentWrapper>
          <Container maxWidth="lg">
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<MenuIcon />} 
                  onClick={handleDrawerToggle}
                  sx={{ color: primaryColor, borderColor: primaryColor }}
                >
                  Menu
                </Button>
              </Box>
            )}
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: { xs: 1, sm: 3 }, mb: 3, borderLeft: `4px solid ${primaryColor}` }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2 }}>
                    <Typography variant="h6" component="h2" fontWeight="bold" color="primary" sx={{ mb: { xs: 1, sm: 0 } }}>
                      {beekeepingData.title}
                    </Typography>
                  </Box>
                  <Grid container spacing={1} style={{ marginTop: '5px',marginBottom: '5px' }}>
                    <Grid item xs={12} sm={6} md={6} ></Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        size="small"
                        placeholder="Rechercher province..."
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: { xs: '100%', sm: '480px' } }}
                      />
                    </Grid>
                  </Grid>
    
                  {/* Section des indicateurs clés */}
                  <Accordion expanded={openKeyStats} onChange={() => setOpenKeyStats(!openKeyStats)} sx={{ mb: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Indicateurs Clés par Province
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Ménages Agricoles Totaux
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {beekeepingData.total.total}
                              </Typography>
                              <Typography variant="caption">
                                pratiquant l'apiculture
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Type d'apiculture dominant
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {keyStats.mostCommonType}
                              </Typography>
                              <Typography variant="body1">
                                {beekeepingData.total[keyStats.mostCommonType.toLowerCase().split(' ')[1]]}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Province avec plus d'apiculture
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {keyStats.provinceWithMostBeekeeping.name}
                              </Typography>
                              <Typography variant="body1">
                                {keyStats.provinceWithMostBeekeeping.traditionnelle} (trad.) + {keyStats.provinceWithMostBeekeeping.moderne} (mod.)
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Plus forte proportion moderne
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {keyStats.highestModernPercentage.province.name}
                              </Typography>
                              <Typography variant="body1">
                                {keyStats.highestModernPercentage.percentage.toFixed(1)}%
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  
                  <TableContainer component={Paper} sx={{ mb: 2, overflowX: 'auto' }}>
                    <Table size="small" aria-label="Tableau des ménages agricoles pratiquant l'apiculture">
                      <StyledTableHead>
                        <TableRow>
                          <TableCell>Province</TableCell>
                          {beekeepingData.types.map((type, index) => (
                            <TableCell key={index} align="right">{type}</TableCell>
                          ))}
                          <TableCell align="right">Total ménages agricoles</TableCell>
                        </TableRow>
                      </StyledTableHead>
                      <TableBody>
                        {/* Ligne pour le total Burundi */}
                        <ProvinceRow hover>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            BURUNDI
                          </TableCell>
                          {beekeepingData.types.map((type, index) => (
                            <TableCell key={index} align="right" sx={{ fontWeight: 'bold' }}>
                              {beekeepingData.total[type.toLowerCase().split(' ')[1]]}
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{beekeepingData.total.total}</TableCell>
                        </ProvinceRow>

                        {/* Données par province */}
                        {filteredProvinces.map((province, index) => (
                          <ProvinceRow key={index} hover>
                            <TableCell component="th" scope="row">
                              {province.name}
                            </TableCell>
                            <TableCell align="right">{province.traditionnelle}</TableCell>
                            <TableCell align="right">{province.moderne}</TableCell>
                            <TableCell align="right">{province.total}</TableCell>
                          </ProvinceRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                     {/*
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Tooltip title="Copier le tableau">
                      <IconButton onClick={handleCopyTable} sx={{ color: primaryColor }}>
                        <FileCopy />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Télécharger les données">
                      <IconButton 
                        onClick={handleDownloadMenuOpen}
                        sx={{ color: secondaryColor }}
                      >
                        <Download />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={downloadAnchorEl}
                      open={Boolean(downloadAnchorEl)}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={() => downloadFile('xlsx')}>
                        <ListItemIcon>
                          <InsertDriveFile fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Excel (.xlsx)</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => downloadFile('csv')}>
                        <ListItemIcon>
                          <Description fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>CSV (.csv)</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => downloadFile('pdf')}>
                        <ListItemIcon>
                          <PictureAsPdf fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>PDF (.pdf)</ListItemText>
                      </MenuItem>
                    </Menu>
                    
                    <Tooltip title="Partager">
                      <IconButton 
                        onClick={handleShareMenuOpen}
                        sx={{ color: primaryColor }}
                      >
                        <Share />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={shareAnchorEl}
                      open={Boolean(shareAnchorEl)}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={() => shareVia('whatsapp')}>
                        <ListItemIcon>
                          <WhatsApp fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>WhatsApp</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => shareVia('email')}>
                        <ListItemIcon>
                          <Email fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Email</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => shareVia('bluetooth')}>
                        <ListItemIcon>
                          <Bluetooth fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Bluetooth</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => shareVia('other')}>
                        <ListItemIcon>
                          <MoreVert fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Autre méthode</ListItemText>
                      </MenuItem>
                    </Menu>
                  </Box>
                   */}
                </Paper>
               
                {/* Section d'information */}
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" color="primary">
                      À propos de l'apiculture
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" paragraph>
                      L'apiculture est une activité agricole importante au Burundi, pratiquée selon deux méthodes principales:
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      <ListItem>
                        <ListItemText 
                          primary="• Apiculture traditionnelle - Utilisation de ruches traditionnelles (en paille, en bois ou en terre)" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• Apiculture moderne - Utilisation de ruches modernes (ruches à cadres comme les ruches Langstroth ou Kenya)" 
                        />
                      </ListItem>
                    </List>
                    <Typography variant="body2" mt={2}>
                      Ces données représentent {beekeepingData.total.total} ménages agricoles au Burundi, 
                      dont {beekeepingData.total.traditionnelle} pratiquent l'apiculture traditionnelle et {beekeepingData.total.moderne} l'apiculture moderne.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                
                {/* Notes méthodologiques */}
                <Paper sx={{ p: 2, backgroundColor: '#f0f0f0', borderLeft: `4px solid ${secondaryColor}` }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Notes méthodologiques
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Les données présentées sont issues du Recensement Général de la Population, de l'Habitat, de l'Agriculture et de l'Elevage (RGPHAE) 
                    réalisé en 2024. Un ménage agricole est comptabilisé comme pratiquant l'apiculture s'il possède au moins une ruche en activité. 
                    La distinction entre apiculture traditionnelle et moderne est basée sur le type de ruche utilisé.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </ContentWrapper>
      </MainContentWithDrawer>
      
      {/* Pied de page */}
      <Footer />
    </MainContainer>
  );
};

export default BeekeepingHouseholdsPage;